import { find, get, isEmpty, isNil, differenceWith, isEqual } from 'lodash/fp';
import { createSelector } from '@reduxjs/toolkit';
import { FetchStatus } from 'app/lib/constants';
import {
    selectValidationValueBySourceFqn,
    selectValidationsValuesBySourceFqn
} from 'app/store/access/selectors';
import {
    selectAllUpstreamSourceFqns,
    selectSelectedEntityId,
    selectSelectedEntityFqn
} from 'app/store/projects/selectors.js';
import { selectCurrentViewId } from 'app/store/view/selectors.js';
import { selectEntitiesById } from 'app/store/entities/selectors.js';
import { DeployingStatus } from './constants';

const selectSlice = (state) => state.deployView;
const selectDeployment = createSelector([selectSlice], ({ deployment }) => deployment);

const selectDeployingByRequestId = createSelector(
    [selectDeployment],
    ({ byRequestId }) => byRequestId
);
export const selectIsDeploymentDialogOpen = createSelector(
    [selectDeployment],
    ({ isDialogOpen }) => isDialogOpen
);
export const selectDeployViewError = createSelector([selectSlice], ({ error }) => error);
export const selectInDeploymentDatasources = createSelector(
    [selectDeployment],
    ({ inDeployment }) => inDeployment
);

export const selectNonDeployingUpstreamSourceFqns = createSelector(
    [selectAllUpstreamSourceFqns, selectInDeploymentDatasources],
    (upstreams, inDeploymentDatasources) => {
        if (isEmpty(inDeploymentDatasources)) {
            return upstreams;
        }
        return differenceWith(isEqual, upstreams, inDeploymentDatasources);
    }
);

const selectViewUuid = createSelector(
    [selectDeployment, selectCurrentViewId, selectSelectedEntityId],
    ({ viewUuid }, currentViewId, selectedViewId) => viewUuid || currentViewId || selectedViewId
);

export const selectViewByUuid = createSelector(
    selectEntitiesById,
    selectViewUuid,
    (entitiesById, viewUuid) => entitiesById?.[viewUuid]
);

export const selectIsEditingSchedule = createSelector(selectSlice, get('isEditingSchedule'));
export const selectIsSavingSchedule = createSelector(selectSlice, get('isSavingSchedule'));

export const selectHistory = createSelector(selectSlice, get('history'));

export const selectHistoryEntity = createSelector([selectHistory], ({ entity }) => entity);

export const selectIsHistoryDialogOpen = createSelector(selectHistoryEntity, ({ fqn }) =>
    Boolean(fqn)
);

const selectDeployedSourceId = createSelector(
    [selectHistoryEntity],
    ({ deployedSourceId }) => deployedSourceId
);

export const selectIsLoadingHistoryForSelectedEntityFqn = createSelector(
    [selectHistory, selectHistoryEntity],
    (history, { fqn }) => {
        const { statusById } = history;

        if (isNil(fqn)) {
            return false;
        }

        return statusById[fqn] === FetchStatus.FETCHING;
    }
);

export const selectEntityForSelectedId = createSelector(
    selectDeployedSourceId,
    selectEntitiesById,
    (selectedEntityId, entitiesById) => entitiesById[selectedEntityId]
);

export const selectHistoryForSelectedId = createSelector(
    selectHistoryEntity,
    selectHistory,
    ({ fqn }, history) => {
        const { resultsById } = history;

        if (isNil(fqn)) {
            return null;
        }

        return resultsById[fqn]?.all ?? resultsById[fqn] ?? null;
    }
);

export const selectRunSummaryForPropFqn = createSelector(
    selectValidationValueBySourceFqn,
    (validationValueBySourceFqn) => {
        if (!validationValueBySourceFqn) {
            return null;
        }

        const { lastStatus, lastRun, nextRun } = validationValueBySourceFqn;

        return {
            lastStatus,
            lastRun,
            nextRun
        };
    }
);

export const selectExecutionHistoryError = createSelector(selectHistory, get('error'));

export const selectIsDeploymentInProgressForNotAccessibleSelectedEntityId = createSelector(
    selectInDeploymentDatasources,
    selectValidationsValuesBySourceFqn,
    selectSelectedEntityFqn,
    (inDeploymentDatasources, accessValidationsValuesBySourceFqn, selectedEntityFqn) => {
        if (!isEmpty(inDeploymentDatasources) && !isNil(selectedEntityFqn)) {
            const { database, schema, table } = selectedEntityFqn;
            const inProgressDataSource = find({ database, schema, table }, inDeploymentDatasources);

            if (!isNil(inProgressDataSource)) {
                const accessValidationsValues = Object.values(accessValidationsValuesBySourceFqn);
                const validationValue = find({ database, schema, table }, accessValidationsValues);
                return !validationValue?.accessible;
            }
        }
        return false;
    }
);

export const selectIsDeployingByView = createSelector(
    selectDeployingByRequestId,
    selectViewByUuid,
    (deployingByRequestId, view) => {
        if (!view) {
            return false;
        }

        const { deployedFrom, name, uuid } = view;
        const hasUpstreamSource = Boolean(deployedFrom);
        const prediction = hasUpstreamSource
            ? { transformationId: deployedFrom, table: name }
            : { transformationId: uuid, status: DeployingStatus.STARTED, isFirstDeploy: true };

        return Boolean(find(prediction, deployingByRequestId));
    }
);
